/** Provenance block for chat table exports.
 *
 *  Pure assembly of the text that accompanies every exported table — the
 *  `Source` sheet and workbook properties of an .xlsx, the caption and footer
 *  of a PDF. No React, no I/O.
 *
 *  Spec: docs/superpowers/specs/2026-09-22-chat-table-exports-design.md,
 *  "Provenance" under Cross-cutting contracts. The wording below is taken from
 *  it verbatim and must stay that way — see PROVENANCE_CAVEAT_LINES. */

/** The transcription caveat, verbatim from the spec.
 *
 *  WHY this is load-bearing, and why you must not soften it: the table data was
 *  retyped by the language model from a tool result truncated to ~3,200
 *  characters before the model ever saw it (`agent_loop.py:194-226`). No full
 *  dataset exists anywhere on the export path and the true source row count is
 *  recorded nowhere. We ship an authoritative-looking workbook — typed cells,
 *  styled header, company name in the document properties — around figures with
 *  that provenance, and this paragraph is the only mitigation in the design
 *  ("Risks accepted" #1).
 *
 *  The wording is plain-firm on purpose: soft wording gets ignored, and legal
 *  wording gets ignored AND reads as boilerplate. It names the actual failure
 *  mode — transcription — and says what to do about it. Change the spec before
 *  you change this. */
export const PROVENANCE_CAVEAT_LINES: readonly string[] = [
  "This table was transcribed by an AI assistant from a chat answer.",
  "It is not a verified extract from a system of record. Check figures",
  "against the source system before relying on them.",
];

/** Shown when the `[…truncated` marker was found in the source body.
 *
 *  Annotate, never block: the detection is a substring heuristic, so false
 *  positives are possible, and refusing someone their own data because we are
 *  unsure is a worse failure than handing it over clearly marked. */
export const TRUNCATION_WARNING =
  "Warning: the source data was truncated before this table was produced. " +
  "Rows are missing.";

export interface ProvenanceInput {
  /** Tenant name, or null where none is resolvable (the line is then omitted). */
  company: string | null;
  /** The user turn that produced this table. Null for the surfaces with no user
   *  turn to attribute — ArtifactFileCard, SubagentResultPreview. */
  question: string | null;
  /** When the export was taken. Rendered as ISO 8601. */
  timestamp: Date;
  /** Rows in THIS table. Makes no claim about the source result set. */
  rowCount: number;
  /** Chat session id, or null. Null is routine in the embed, not an error. */
  sessionId: string | null;
  /** Whether the `[…truncated` marker was detected in the source body. */
  truncated: boolean;
}

const LABEL_WIDTH = 12;

function field(label: string, value: string): string {
  return `${label}:`.padEnd(LABEL_WIDTH) + value;
}

function present(value: string | null): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

/** Build the provenance block as lines.
 *
 *  Absent fields drop their line entirely. We never print a placeholder: in the
 *  embed `sessionId` is null until `onSessionEstablished` fires mid-stream and
 *  the backend mints a fresh session per turn, so absence is the normal case
 *  there — and a literal "null" or "unknown" in a finance export reads as a bug
 *  in the data rather than as a field we simply do not have. */
export function buildProvenance(input: ProvenanceInput): string[] {
  const lines: string[] = [];

  if (input.truncated) {
    lines.push(TRUNCATION_WARNING, "");
  }

  lines.push("Exported from Artie chat");

  const company = present(input.company);
  if (company) lines.push(field("Company", company));

  const question = present(input.question);
  if (question) lines.push(field("Question", `"${question}"`));

  lines.push(field("Exported", input.timestamp.toISOString()));
  const noun = input.rowCount === 1 ? "row" : "rows";
  lines.push(field("Rows", `${input.rowCount} ${noun} in this table`));

  const sessionId = present(input.sessionId);
  if (sessionId) lines.push(field("Session", sessionId));

  lines.push("", ...PROVENANCE_CAVEAT_LINES);

  return lines;
}
